import React, { useEffect, useState } from 'react';
import { DownloadCloud, RefreshCw, X } from 'lucide-react';
import { registerSW } from 'virtual:pwa-register';
import { useUIStore } from '../../stores/uiStore';
import { Button } from '../ui/Button';

const RELOAD_GUARD = 'masar-pwa-update-reload';

export const PwaUpdatePrompt: React.FC = () => {
  const lang = useUIStore(state => state.lang);
  const [needRefresh, setNeedRefresh] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [update, setUpdate] = useState<((reloadPage?: boolean) => Promise<void>) | null>(null);
  const isRTL = lang === 'ar';

  useEffect(() => {
    const stored = Number(sessionStorage.getItem(RELOAD_GUARD) ?? 0);
    if (stored && Date.now() - stored > 60_000) sessionStorage.removeItem(RELOAD_GUARD);
    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh: () => setNeedRefresh(true),
      onOfflineReady: () => setOfflineReady(true),
      onRegisterError: (caught: unknown) => setError(caught instanceof Error ? caught.message : 'Service worker registration failed'),
    });
    setUpdate(() => updateSW);
  }, []);

  if (!needRefresh && !offlineReady && !error) return null;

  const applyUpdate = async () => {
    if (!update || busy) return;
    const lastReload = Number(sessionStorage.getItem(RELOAD_GUARD) ?? 0);
    if (lastReload && Date.now() - lastReload < 30_000) {
      setError(isRTL ? 'تم طلب التحديث بالفعل. أعد المحاولة بعد لحظات.' : 'Update already requested. Try again in a moment.');
      return;
    }
    setBusy(true); setError('');
    try {
      sessionStorage.setItem(RELOAD_GUARD, String(Date.now()));
      await update(true);
    } catch (caught) {
      sessionStorage.removeItem(RELOAD_GUARD);
      setError(caught instanceof Error ? caught.message : (isRTL ? 'تعذر تثبيت التحديث' : 'Could not install the update'));
      setBusy(false);
    }
  };

  return (
    <section className="pwa-prompt glass-card" role="status" aria-live="polite" aria-label={isRTL ? 'تحديث التطبيق' : 'Application update'}>
      <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-gold-500/15 text-gold-300"><DownloadCloud size={21} /></span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-champagne-100">{needRefresh ? (isRTL ? 'نسخة MASAR جديدة جاهزة' : 'A new MASAR version is ready') : (isRTL ? 'MASAR جاهز للعمل دون اتصال' : 'MASAR is ready offline')}</p>
        <p className="mt-0.5 text-xs text-champagne-400">{error || (needRefresh ? (isRTL ? 'ثبّت الأصول الجديدة وأعد التحميل مرة واحدة بأمان.' : 'Install the new assets and reload safely once.') : (isRTL ? 'تم حفظ الأصول الأساسية على هذا الجهاز.' : 'Core assets are cached on this device.'))}</p>
        {needRefresh && <Button className="mt-3" size="sm" variant="gold" icon={<RefreshCw size={14} />} loading={busy} onClick={() => void applyUpdate()}>{isRTL ? 'تحديث الآن' : 'Update now'}</Button>}
      </div>
      <button type="button" className="icon-control" onClick={() => { setNeedRefresh(false); setOfflineReady(false); setError(''); }} aria-label={isRTL ? 'إغلاق إشعار التحديث' : 'Dismiss update notification'}><X size={17} /></button>
    </section>
  );
};
